import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { book } from '../content/book.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Posts to MailerLite via fetch with no-cors. Falls back to inline success state
// regardless of network response (MailerLite returns opaque to cross-origin js).
export default function NewsletterForm({ classes = {}, showPrivacy = true }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const formRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined' || window.__mlLoaded) return;
    const s = document.createElement('script');
    s.src = book.mailerLite.universalScript;
    s.async = true;
    s.onload = () => {
      window.__mlLoaded = true;
      try {
        window.ml &&
          window.ml('account', book.mailerLite.accountId);
      } catch {
        /* ignore */
      }
    };
    document.head.appendChild(s);
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email)) {
      setStatus('error');
      return;
    }
    setStatus('submitting');
    try {
      const fd = new FormData();
      fd.append('fields[email]', email);
      fd.append('ml-submit', '1');
      fd.append('anticsrf', 'true');
      await fetch(book.mailerLite.formAction, {
        method: 'POST',
        mode: 'no-cors',
        body: fd,
      });
    } catch {
      /* MailerLite returns opaque; treat as success */
    }
    setStatus('success');
  };

  const c = {
    form: '',
    label: '',
    inputWrap: '',
    input: '',
    button: '',
    privacy: '',
    success: '',
    error: '',
    ...classes,
  };

  if (status === 'success') {
    return (
      <div className={c.success} role="status" aria-live="polite">
        {t('newsletter.success')}
      </div>
    );
  }

  return (
    <form
      ref={formRef}
      onSubmit={onSubmit}
      className={c.form}
      action={book.mailerLite.formAction}
      method="post"
      target="_blank"
      noValidate
    >
      <div className={c.inputWrap}>
        <label htmlFor="newsletter-email" className={c.label}>
          {t('newsletter.placeholder')}
        </label>
        <input
          id="newsletter-email"
          name="fields[email]"
          type="email"
          autoComplete="email"
          inputMode="email"
          required
          placeholder={t('newsletter.placeholder')}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (status === 'error') setStatus('idle');
          }}
          aria-invalid={status === 'error'}
          className={c.input}
        />
        <input type="hidden" name="ml-submit" value="1" />
        <input type="hidden" name="anticsrf" value="true" />
        <button
          type="submit"
          className={c.button}
          disabled={status === 'submitting'}
        >
          {status === 'submitting' ? t('newsletter.submitting') : t('newsletter.submit')}
        </button>
      </div>
      {status === 'error' && (
        <p className={c.error} role="alert">
          {t('newsletter.error')}
        </p>
      )}
      {showPrivacy && status !== 'error' && (
        <p className={c.privacy}>{t('newsletter.privacy')}</p>
      )}
    </form>
  );
}
